import { useCallback, useMemo } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetMeQuery } from '../../store/user/user'
import { type User } from '../../models/User'
import { useIntegrations } from './useIntegrations'

export const useCurrentUserIntegrations = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetMeQuery({ accountId })
  const {
    canExportMeeting,
    supportsMeetingSummaryExport,
    supportsTaskExport,
    supportsDocumentSummaryExport,
    supportsDocumentUpload,
    canCreateOrUpdateContact,
    isLoading: isIntegrationsLoading,
  } = useIntegrations()

  const meetingExportSupportedIntegrations = useMemo(
    () => currentUser?.integrations?.filter(({ serviceId }) => canExportMeeting(serviceId)) ?? [],
    [currentUser, canExportMeeting],
  )

  const meetingSummaryExportSupportedIntegrations = useMemo(
    () => currentUser?.integrations?.filter(({ serviceId }) => supportsMeetingSummaryExport(serviceId)) ?? [],
    [currentUser, supportsMeetingSummaryExport],
  )

  const taskExportSupportedIntegrations = useMemo(
    () => currentUser?.integrations?.filter(({ serviceId }) => supportsTaskExport(serviceId)) ?? [],
    [currentUser, supportsTaskExport],
  )

  const documentSummaryExportSupportedIntegrations = useMemo(
    () => currentUser?.integrations?.filter(({ serviceId }) => supportsDocumentSummaryExport(serviceId)) ?? [],
    [currentUser, supportsDocumentSummaryExport],
  )

  const documentUploadSupportedIntegrations = useMemo(
    () => currentUser?.integrations?.filter(({ serviceId }) => supportsDocumentUpload(serviceId)) ?? [],
    [currentUser, supportsDocumentUpload],
  )

  const getContactCreateOrUpdateSupportedIntegrations = useCallback(
    (contact: User) => currentUser?.integrations?.filter(({ serviceId }) => canCreateOrUpdateContact(serviceId, contact)) ?? [],
    [currentUser, canCreateOrUpdateContact],
  )

  return {
    meetingExportSupportedIntegrations,
    meetingSummaryExportSupportedIntegrations,
    taskExportSupportedIntegrations,
    documentSummaryExportSupportedIntegrations,
    documentUploadSupportedIntegrations,
    getContactCreateOrUpdateSupportedIntegrations,
    isLoading: isIntegrationsLoading || isCurrentUserLoading,
  }
}
